import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, Search, UserPlus, ShieldCheck, ShieldOff, Trash2, KeyRound, RefreshCw, LogOut, Coins } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const PLANS = ["free", "trial", "starter", "professional", "enterprise"];
const ROLES = ["user", "admin", "super_admin"];
const STATUSES = ["active", "suspended", "deleted"];

const StatusBadge = ({ status }) => {
  const map = { active: "text-[#34D399] border-[#34D399]/40", suspended: "text-[#F59E0B] border-[#F59E0B]/40", deleted: "text-red-400 border-red-400/40" };
  return <span className={`text-[10px] px-1.5 py-0.5 border ${map[status] || "text-white/40 border-white/20"} rounded-sm uppercase font-mono`}>{status}</span>;
};

const RoleBadge = ({ role }) => {
  const map = { super_admin: "text-[#8A52FF] border-[#8A52FF]/40", admin: "text-[#00E5FF] border-[#00E5FF]/40", user: "text-white/60 border-white/20" };
  return <span className={`text-[10px] px-1.5 py-0.5 border ${map[role] || map.user} rounded-sm uppercase font-mono`}>{role}</span>;
};

export default function AdminUsers() {
  const nav = useNavigate();
  const { user: current } = useAuth();
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [plan, setPlan] = useState("all");
  const [status, setStatus] = useState("all");
  const [skip, setSkip] = useState(0);
  const [limit] = useState(30);
  const [createOpen, setCreateOpen] = useState(false);

  const isSuper = current?.role === "super_admin";

  const load = async () => {
    const params = { skip, limit };
    if (q) params.q = q;
    if (role !== "all") params.role = role;
    if (plan !== "all") params.plan = plan;
    if (status !== "all") params.status = status;
    try {
      const { data } = await api.get("/admin/users", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load users");
    }
  };
  useEffect(() => { load(); }, [skip, role, plan, status]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]);

  return (
    <div className="p-8 max-w-[1600px]">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kr-overline text-[#8A52FF]">[ SUPER ADMIN · USER DIRECTORY ]</div>
          <h1 className="font-display text-4xl mt-2 tracking-tight" data-testid="admin-users-title">Users</h1>
          <p className="text-white/55 text-sm mt-1.5">{total.toLocaleString()} total accounts. Manage roles, plans, credits, sessions and access.</p>
        </div>
        {isSuper && (
          <Button onClick={() => setCreateOpen(true)} className="bg-[#8A52FF] hover:bg-[#8A52FF]/90 rounded-full" data-testid="admin-users-create-btn">
            <UserPlus className="h-4 w-4 mr-2" /> Create user
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[260px]">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-white/30" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or user ID…" className="pl-8 h-9 bg-[#050505] border-white/10" data-testid="admin-users-search" />
        </div>
        <Select value={role} onValueChange={(v) => { setSkip(0); setRole(v); }}>
          <SelectTrigger className="w-[140px] h-9 bg-[#050505] border-white/10 text-sm" data-testid="admin-users-role-filter"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent className="bg-[#0A0A0E] border-white/10 text-white"><SelectItem value="all">All roles</SelectItem>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={plan} onValueChange={(v) => { setSkip(0); setPlan(v); }}>
          <SelectTrigger className="w-[140px] h-9 bg-[#050505] border-white/10 text-sm" data-testid="admin-users-plan-filter"><SelectValue placeholder="Plan" /></SelectTrigger>
          <SelectContent className="bg-[#0A0A0E] border-white/10 text-white"><SelectItem value="all">All plans</SelectItem>{PLANS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setSkip(0); setStatus(v); }}>
          <SelectTrigger className="w-[140px] h-9 bg-[#050505] border-white/10 text-sm" data-testid="admin-users-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-[#0A0A0E] border-white/10 text-white"><SelectItem value="all">All statuses</SelectItem>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="mt-4 border border-white/5">
        <table className="w-full text-xs">
          <thead className="bg-[#050505] text-white/50">
            <tr>
              <th className="text-left px-3 py-2.5 font-normal">User</th>
              <th className="text-left px-3 py-2.5 font-normal">Role</th>
              <th className="text-left px-3 py-2.5 font-normal">Plan</th>
              <th className="text-right px-3 py-2.5 font-normal">Credits</th>
              <th className="text-left px-3 py-2.5 font-normal">Status</th>
              <th className="text-left px-3 py-2.5 font-normal">Registered</th>
              <th className="text-left px-3 py-2.5 font-normal">Last activity</th>
              <th className="text-left px-3 py-2.5 font-normal">Last IP</th>
              <th className="text-right px-3 py-2.5 font-normal">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.user_id} onClick={() => nav(`/admin/users/${u.user_id}`)} className="border-t border-white/5 hover:bg-white/[0.02] cursor-pointer" data-testid={`admin-user-row-${u.user_id}`}>
                <td className="px-3 py-2.5">
                  <div className="text-white">{u.name || "—"}</div>
                  <div className="text-white/40 text-[10px] font-mono">{u.email}</div>
                </td>
                <td className="px-3 py-2.5"><RoleBadge role={u.role} /></td>
                <td className="px-3 py-2.5 uppercase font-mono text-white/70">{u.plan}</td>
                <td className="px-3 py-2.5 text-right font-mono">{Number(u.credits || 0).toLocaleString()}</td>
                <td className="px-3 py-2.5"><StatusBadge status={u.status || "active"} /></td>
                <td className="px-3 py-2.5 text-white/50 font-mono">{(u.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2.5 text-white/50 font-mono">{u.last_activity_at ? new Date(u.last_activity_at).toLocaleDateString() : "—"}</td>
                <td className="px-3 py-2.5 text-white/50 font-mono">{u.last_login_ip || u.last_activity_ip || "—"}</td>
                <td className="px-3 py-2.5 text-right">
                  <button onClick={(e) => { e.stopPropagation(); nav(`/admin/users/${u.user_id}`); }} className="text-[#00E5FF] hover:underline">Open →</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-12 text-center text-white/40">No users match those filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-3 flex items-center justify-between text-xs text-white/50">
        <div>Showing {skip + 1}–{Math.min(skip + limit, total)} of {total}</div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setSkip(Math.max(0, skip - limit))} disabled={skip === 0} className="border-white/10 h-7" data-testid="admin-users-prev">Previous</Button>
          <Button variant="outline" onClick={() => setSkip(skip + limit)} disabled={skip + limit >= total} className="border-white/10 h-7" data-testid="admin-users-next">Next</Button>
        </div>
      </div>

      {createOpen && <CreateUserDialog onClose={() => setCreateOpen(false)} onCreated={load} />}
    </div>
  );
}

function CreateUserDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "user", plan: "free", credits: 500, must_change_password: true });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", form);
      toast.success(`Created ${data.user.email}. Temporary password: ${data.temporary_password}`, { duration: 15000 });
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create user");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0E] border-white/10 text-white max-w-lg" data-testid="admin-users-create-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><UserPlus className="h-5 w-5 text-[#8A52FF]" /> Create user</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div><label className="kr-overline">Full name</label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 bg-[#050505] border-white/10" data-testid="admin-create-name" /></div>
          <div><label className="kr-overline">Email</label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1 bg-[#050505] border-white/10" data-testid="admin-create-email" /></div>
          <div><label className="kr-overline">Temporary password</label><Input type="text" value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1 bg-[#050505] border-white/10 font-mono" data-testid="admin-create-password" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="kr-overline">Role</label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger className="mt-1 bg-[#050505] border-white/10" data-testid="admin-create-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0E] border-white/10 text-white">{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="kr-overline">Plan</label>
              <Select value={form.plan} onValueChange={(v) => set("plan", v)}>
                <SelectTrigger className="mt-1 bg-[#050505] border-white/10" data-testid="admin-create-plan"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0E] border-white/10 text-white">{PLANS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="kr-overline">Credits</label><Input type="number" value={form.credits} onChange={(e) => set("credits", Number(e.target.value))} className="mt-1 bg-[#050505] border-white/10 font-mono" data-testid="admin-create-credits" /></div>
          </div>
          <label className="flex items-center gap-2 text-xs text-white/70 pt-1">
            <input type="checkbox" checked={form.must_change_password} onChange={(e) => set("must_change_password", e.target.checked)} data-testid="admin-create-must-change" /> Require password change at first login
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-white/10">Cancel</Button>
          <Button onClick={submit} disabled={busy || !form.email || !form.password} className="bg-[#8A52FF] hover:bg-[#8A52FF]/90" data-testid="admin-create-submit">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
